import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Terminal, LayoutDashboard, Radar, Bug, FileText, Skull, ShieldCheck, CreditCard, LogOut, Crown, Users, CalendarClock, Menu, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard",       label: "Command Center", icon: LayoutDashboard, code: "01" },
  { to: "/scanner",         label: "Recon & Scan",   icon: Radar,           code: "02" },
  { to: "/terminal",        label: "Live Ops Term",  icon: Terminal,        code: "03" },
  { to: "/vulnerabilities", label: "Vulnerabilities", icon: Bug,            code: "04" },
  { to: "/exploits",        label: "Remediation",    icon: ShieldCheck,     code: "05" },
  { to: "/reports",         label: "Security Report", icon: FileText,       code: "06" },
  { to: "/schedules",       label: "Scans planifiés", icon: CalendarClock,  code: "07" },
  { to: "/tools",           label: "Santé des outils", icon: Activity,      code: "08" },
  { to: "/pricing",         label: "Formules",       icon: CreditCard,      code: "09" },
];

const ADMIN_NAV = { to: "/admin", label: "Admin Abonnés", icon: Users, code: "10" };

const navId = (to) => `nav-${to.replace("/", "")}`;

function NavItems({ pathname, isAdmin, onNavigate }) {
  const items = [...NAV, ...(isAdmin ? [ADMIN_NAV] : [])];
  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        const active = pathname === item.to;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.to === ADMIN_NAV.to ? "nav-admin" : navId(item.to)}
            className={cn(
              "group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium relative transition-colors duration-200",
              active
                ? "bg-primary/10 text-foreground border-l-2 border-primary"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-l-2 border-transparent"
            )}
          >
            <span className="font-mono text-[10px] text-muted-foreground group-hover:text-primary">{item.code}</span>
            <Icon className={cn("h-4 w-4", active && "text-primary")} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </>
  );
}

function Brand() {
  return (
    <NavLink to="/" className="flex items-center gap-3 group">
      <div className="relative">
        <div className="absolute inset-0 bg-primary/30 blur-lg group-hover:bg-primary/50 transition-colors" />
        <div className="relative h-9 w-9 rounded-md bg-gradient-danger flex items-center justify-center border border-primary/40">
          <Skull className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
        </div>
      </div>
      <div>
        <div className="font-mono text-xs text-muted-foreground tracking-widest">BLUE-TEAM</div>
        <div className="font-bold text-base tracking-tight leading-none">CRIMSON<span className="text-primary">.OPS</span></div>
      </div>
    </NavLink>
  );
}

const StatusCard = () => (
  <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
    <div className="flex items-center gap-2 mb-2">
      <span className="h-2 w-2 rounded-full bg-success pulse-dot" />
      <span className="font-mono text-[10px] tracking-widest text-success">SCANNER PRÊT</span>
    </div>
    <div className="font-mono text-[11px] text-muted-foreground leading-relaxed">
      mode: <span className="text-foreground">défensif</span><br />
      moteur: <span className="text-foreground">nmap 7.93</span><br />
      cibles: <span className="text-foreground">autorisées</span>
    </div>
  </div>
);

const AppLayout = () => {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const plan = (user && user.plan) || "free";
  const isAdmin = user?.role === "admin";

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/40 backdrop-blur-sm sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border"><Brand /></div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="px-2 pb-2 text-[10px] font-mono tracking-widest text-muted-foreground">// OPERATIONS</div>
          <NavItems pathname={pathname} isAdmin={isAdmin} />
        </nav>
        <div className="p-3 border-t border-border"><StatusCard /></div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
          <div className="flex items-center justify-between px-4 md:px-6 h-14">
            <div className="flex items-center gap-3">
              {/* Mobile menu */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="md:hidden h-9 w-9 border-border" data-testid="mobile-menu-btn">
                    <Menu className="h-4 w-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 bg-card border-border p-0 flex flex-col" data-testid="mobile-nav">
                  <div className="px-5 py-5 border-b border-border"><Brand /></div>
                  <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                    <div className="px-2 pb-2 text-[10px] font-mono tracking-widest text-muted-foreground">// OPERATIONS</div>
                    <NavItems pathname={pathname} isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} />
                  </nav>
                  <div className="p-3 border-t border-border"><StatusCard /></div>
                </SheetContent>
              </Sheet>

              <div className="md:hidden flex items-center gap-2">
                <div className="h-7 w-7 rounded bg-gradient-danger flex items-center justify-center">
                  <Skull className="h-4 w-4 text-primary-foreground" />
                </div>
                <span className="font-bold text-sm">CRIMSON<span className="text-primary">.OPS</span></span>
              </div>
              <div className="hidden md:flex items-center gap-2 font-mono text-xs text-muted-foreground">
                <span className="text-primary">$</span>
                <span>~/crimson-ops/defensive-scanner</span>
                <span className="terminal-caret"></span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn("font-mono text-[10px] gap-1.5", plan === "pro" ? "border-primary/50 text-primary" : plan === "standard" ? "border-info/50 text-info" : "border-border text-muted-foreground")} data-testid="plan-badge">
                <Crown className="h-3 w-3" /> {plan.toUpperCase()}{user?.is_trial ? " · ESSAI" : ""}
              </Badge>
              {plan !== "pro" && (
                <NavLink to="/pricing"><Button size="sm" className="font-mono text-[10px] bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 hidden sm:inline-flex" data-testid="upgrade-btn"><CreditCard className="h-3 w-3" /> UPGRADE</Button></NavLink>
              )}
              <span className="hidden md:inline font-mono text-xs text-muted-foreground max-w-[140px] truncate" data-testid="user-email">{user?.email}</span>
              <Button onClick={doLogout} variant="outline" size="sm" className="font-mono text-xs border-border gap-1.5" data-testid="logout-btn">
                <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sortir</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
